import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ObjectID } from "bson";
import React, { useRef } from "react";

import { faExclamationCircle, faGripLines, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";

//hooks
import {
	ArrayFieldState,
	ButtonStandard,
	FormField,
	FormInputText,
	FormList,
	Icon,
	Text,
	useKeyPress,
} from "@performyard/py-components";
//utils
import { Checkbox } from "@performyard/py-components/src/components/atoms/Checkbox";
import { MultipleChoiceOptionJson, SurveyQuestionJson } from "@performyard/service-contracts";

//components
import IconButton from "../../../../../../../../../components/Button/IconButton";
import DragAndDrop from "../../../../../../../../../components/DragAndDrop";
import Spacer from "../../../../../../../../../components/Spacer";

const MAX_CHOICES = 20;

type MultipleChoiceRowProps = {
	index: number;
	parentIndex: number;
	field: ArrayFieldState<MultipleChoiceOptionJson>;
};

const MultipleChoiceRow = ({ index, field, parentIndex }: MultipleChoiceRowProps) => {
	const { attributes, listeners, setNodeRef, transform } = useSortable({ id: `${index}` });

	const style = {
		transform: CSS.Translate.toString(transform),
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="multiple-choice-row flex gap-2"
			data-testid="multiple-option-row"
		>
			<div
				{...listeners}
				className="flex h-8 w-6 flex-shrink-0 cursor-grab items-center justify-center border-r border-neutral-2"
			>
				<Icon color="neutral-7" icon={faGripLines} dangerouslySetClassName="hover:text-neutral-9" />
			</div>

			<div className="flex-grow">
				<FormInputText
					id={`questions[${parentIndex}].options[${index}].text`}
					name={`questions[${parentIndex}].options[${index}].text`}
					placeholder={`Answer Option ${index + 1}`}
				/>
			</div>
			{field?.value?.length > 1 && (
				<IconButton
					style={{ width: 20 }}
					color="gray"
					icon={faXmarkCircle}
					onClick={() => field.removeValue(index)}
					testId="delete-option"
					aria-label="Remove option"
				/>
			)}
		</div>
	);
};

const MultipleChoiceEntries = ({
	field,
	index,
	publishedQuestion,
}: {
	field: ArrayFieldState<MultipleChoiceOptionJson>;
	index: number;
	publishedQuestion: boolean;
}) => {
	const { value, pushValue } = field;

	const [ref] = useAutoAnimate();
	const containerRef = useRef<HTMLDivElement>(null);

	const handleAddOption = () => {
		if (value?.length >= MAX_CHOICES) {
			return;
		}

		const newOption: MultipleChoiceOptionJson = {
			text: "",
			_id: new ObjectID().toString(),
			order: value.length + 1,
		};

		pushValue(newOption);

		// wait 10 ms and then focus on the last input to wait for render
		setTimeout(() => {
			const lastInput = document.getElementById(`questions[${index}].options[${value?.length}].text`);
			if (lastInput) {
				lastInput.focus();
			}
		}, 10);
	};

	useKeyPress({
		key: "Enter",
		onPress: (e) => {
			if (e.target instanceof HTMLInputElement && value?.length < MAX_CHOICES) {
				e.preventDefault();
				e.stopPropagation();
				handleAddOption();
			}
		},
		containerRef,
	});

	return (
		<div ref={containerRef}>
			{value?.length && (
				<DragAndDrop
					itemsContainerRef={ref}
					restrictToParentElement
					restrictToVerticalAxis
					itemsContainerClassName="flex flex-col gap-2"
					handleOnDragEnd={(event) => {
						if (publishedQuestion) return;

						const { active, over } = event;

						if (active.id !== over?.id) {
							const oldIndex = Number(active.id);
							const newIndex = Number(over?.id);
							field.shiftValue(oldIndex, newIndex);
						}
					}}
					itemIds={value.map((_choice, index) => index.toString())}
					draggableNodeItems={value.map((_choice, listIndex) => (
						<MultipleChoiceRow key={`${listIndex}`} index={listIndex} parentIndex={index} field={field} />
					))}
				/>
			)}

			{value?.length >= MAX_CHOICES && (
				<div className="flex items-center gap-2 pt-2">
					<Icon size={16} color="red-6" icon={faExclamationCircle} />
					<Text size="caption" weight="semi-bold" color="red-6">
						You have reached the maximum number of options
					</Text>
				</div>
			)}
			<Spacer height={2} />

			<div className="flex w-full justify-end">
				<ButtonStandard
					id={`add-option-${index}`}
					type="button"
					iconLeft={faPlus}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (value?.length >= MAX_CHOICES || publishedQuestion) {
							return;
						}
						handleAddOption();
					}}
					disabled={value?.length >= MAX_CHOICES || publishedQuestion}
				>
					Add Option
				</ButtonStandard>
			</div>
		</div>
	);
};

const MultipleChoiceEdit = ({
	data,
	index,
	publishedQuestion,
	mutateField,
}: {
	data: SurveyQuestionJson & { isNew?: boolean };
	index: number;
	publishedQuestion: boolean;
	mutateField: (value: SurveyQuestionJson & { isNew?: boolean }) => void;
}) => {
	return (
		<div>
			<Text overrideElement="h6" size="paragraph-3" weight="semi-bold">
				Answer Options
			</Text>
			<Spacer height={1} />
			<FormField<string>
				id={`questions[${index}].type`}
				label={<Text size="paragraph-3">Allow for selecting multiple answers</Text>}
				labelPosition="right"
				input={(field) => (
					<div className="flex items-center gap-2">
						<Checkbox
							id={`questions[${index}].type`}
							disabled={publishedQuestion}
							checked={field.value === "multiple-select"}
							onCheckedChange={() => {
								const newType =
									field.value === "multiple-select" ? "multiple-choice" : "multiple-select";
								field.onChange(newType);
								mutateField({ ...data, type: newType });
							}}
						/>
					</div>
				)}
			/>
			<Spacer height={2} />
			<FormList<MultipleChoiceOptionJson>
				id={`questions[${index}].options`}
				input={(field) => (
					<MultipleChoiceEntries field={field} index={index} publishedQuestion={publishedQuestion} />
				)}
			/>
		</div>
	);
};

export default MultipleChoiceEdit;
